/**
 * The container editor drawer (centered, mock chrome) — and, since Phase
 * 14, the MASTER editor in `mode="master"` (same tabs minus List groups;
 * General holds identity, shared defaults and the Hour Optimizer). Seeds
 * once per key, dirty = JSON compare vs the seeded baseline, close is
 * confirm-guarded. Save keeps the drawer open and re-seeds from the server;
 * a create swaps the URL to the real id. A Save that switches a campaign
 * group's auto-send ON asks first (real SMS — Phase 20). A saved master
 * config can push itself onto its containers (master/push, 21/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';

import { ConfirmDialog, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui';

import type { DropGenPlanDetail } from '../../drop-generator.types';
import { useContainerDetail } from '../../useDropGenerator';
import { containerSetupHints, draftSetupFacts, hintsFor } from '../../utils/setup-hints';
import { AutoSendSaveConfirm } from './autosend/AutoSendSaveConfirm';
import { sendsByItself, switchesGroupOn } from './autosend/autosend-slice';
import { EditorHeader } from './EditorHeader';
import { EditorTabs, type EditorTabKey } from './EditorTabs';
import { MasterPushProvider } from './master/push/master-push-context';
import { SectionSourcesProvider } from './master/section-sources';
import { EditorBody } from './shell/EditorBody';
import { editorPosition } from './shell/editor-position';
import { EditorSaveProvider } from './shell/editor-save-context';
import { useEditorSeed } from './shell/use-editor-seed';
import { validateDraft } from './state/container-draft';
import { type EditorPreset, newDraft } from './state/master/new-draft';
import { useContainerEditor } from './state/useContainerEditor';
import { useContainerSave } from './use-container-save';
import { useDirtyCloseGuard } from './use-dirty-close-guard';

interface Props {
  plan: DropGenPlanDetail;
  countryName: string;
  mode?: 'container' | 'master';
  containerId: string | null;
  onClose: () => void;
  /** After a CREATE: swap ?container=new (or ?master=new) for the real id. */
  onSaved: (id: string) => void;
  /** Phase 14 — jump to a master's editor from a follower's section bar. */
  onOpenMaster?: (id: string | 'new', etab?: EditorTabKey) => void;
  initialTab?: EditorTabKey; // open on this tab (?etab=, tutorial bridges)
  /** What a NEW container / master config starts from (the open campaign). */
  preset?: EditorPreset;
  /** Master mode: the campaign is the page's — shown, never picked. */
  campaignLocked?: boolean;
  /** The campaign group open on the page: its campaigns + master config. */
  groupKey?: string | null;
}

export const ContainerEditorDrawer: FC<Props> = ({
  plan,
  countryName,
  mode = 'container',
  containerId,
  onClose,
  onSaved,
  onOpenMaster = () => undefined,
  initialTab,
  preset,
  campaignLocked = false,
  groupKey = null,
}) => {
  const editor = useContainerEditor();
  const detail = useContainerDetail(containerId);
  const [tab, setTab] = useState<EditorTabKey>(initialTab ?? 'general');
  const presetMasterId = !containerId && mode === 'container' ? (preset?.masterId ?? null) : null;
  const copyId = !containerId && mode === 'master' ? (preset?.copyFromMasterId ?? null) : null;
  const presetMaster = useContainerDetail(presetMasterId);
  const copySource = useContainerDetail(copyId);
  const presetRef = (plan.masters ?? []).find((m) => m.id === presetMasterId) ?? null;
  const pending = (Boolean(presetRef) && !presetMaster.data) || (Boolean(copyId) && !copySource.data);
  const fresh = useMemo(
    () =>
      newDraft(mode, preset, {
        master: presetRef ? { ref: presetRef, config: presetMaster.data?.effective ?? null } : null,
        copy: copySource.data ?? null,
      }),
    [mode, preset?.campaignWiserId, preset?.name, presetRef, presetMaster.data, copySource.data],
  );
  const seedKey = `${plan.countryKey}:${mode}:${containerId ?? `new:${presetMasterId ?? preset?.campaignWiserId ?? ''}:${copyId ?? ''}`}`;
  const { dirty } = useEditorSeed({
    editor,
    detail,
    containerId,
    seedKey,
    fresh,
    pending,
    initialTab,
    setTab,
  });
  const { saving, save } = useContainerSave({
    planId: plan.id,
    countryKey: plan.countryKey,
    containerId,
    editor,
    detail,
    onSaved,
  });
  const invalidReason = validateDraft(editor.draft);
  const guard = useDirtyCloseGuard(onClose, dirty && !saving);
  const position = editorPosition({
    mode,
    containerId,
    plan,
    followerCount: detail.data?.followerCount,
    copyName: copySource.data?.name,
  });
  const [confirmingSend, setConfirmingSend] = useState(false);
  const saveLabel = mode === 'master' ? 'Save master config' : 'Save container';
  const requestSave = () => (switchesGroupOn(editor.draft) ? setConfirmingSend(true) : void save());

  return (
    <Drawer open onOpenChange={(open) => !open && guard.requestClose()}>
      <DrawerContent variant="center" width="w-[min(1320px,96vw)]" className="max-h-[92vh]">
        <MasterPushProvider plan={plan} masterId={mode === 'master' ? containerId : null} editor={editor} edits={{ dirty, invalidReason, save }}>
        <DrawerHeader>
          {/* The visible header is a chip + facts; screen readers get the dialog's name here. */}
          <DrawerTitle className="sr-only">
            {mode === 'master' ? 'Master config' : 'Container'} {editor.draft.name || 'new'}
          </DrawerTitle>
          <EditorHeader
            name={editor.draft.name}
            color={editor.draft.color}
            position={position}
            countryName={countryName}
            dirty={dirty}
            saving={saving}
            invalidReason={invalidReason}
            kind={mode}
            routeTest={editor.draft.kind === 'ROUTE_TEST'}
            saveLabel={saveLabel}
            isNew={!containerId}
            onSave={requestSave}
            onClose={guard.requestClose}
          />
        </DrawerHeader>
        <DrawerBody className="flex flex-col gap-4">
          <EditorSaveProvider
            value={{
              label: saveLabel,
              dirty,
              saving,
              invalidReason,
              save: requestSave,
            }}
          >
            <SectionSourcesProvider key={seedKey} editor={editor} plan={plan} groupKey={groupKey} onOpenMaster={onOpenMaster}>
              <EditorTabs
                value={tab}
                mode={mode}
                routeTest={editor.draft.kind === 'ROUTE_TEST'}
                groupCount={editor.draft.groups.length}
                poolCount={editor.draft.gateway.pool.length}
                missingTabs={hintsFor(mode, containerSetupHints(draftSetupFacts(editor.draft))).map((hint) => hint.tab)}
                autoSendOn={sendsByItself(editor.draft, plan)}
                onChange={setTab}
              />
              <EditorBody
                tab={tab}
                mode={mode}
                editor={editor}
                plan={plan}
                countryName={countryName}
                containerId={containerId}
                campaignLocked={campaignLocked}
                loading={containerId ? !detail.data : pending}
              />
            </SectionSourcesProvider>
          </EditorSaveProvider>
        </DrawerBody>
        </MasterPushProvider>
      </DrawerContent>
      <AutoSendSaveConfirm
        open={confirmingSend}
        plan={plan}
        draft={editor.draft}
        masterId={containerId}
        busy={saving}
        onConfirm={() => {
          setConfirmingSend(false);
          void save();
        }}
        onCancel={() => setConfirmingSend(false)}
      />
      <ConfirmDialog
        open={guard.confirming}
        title="Discard unsaved changes?"
        description={`${mode === 'master' ? 'This master config has' : 'This container has'} edits that were never saved. Closing the editor loses them.`}
        confirmLabel="Discard changes"
        destructive
        onConfirm={guard.discard}
        onCancel={guard.keepEditing}
      />
    </Drawer>
  );
};
